import {
  SerializedClassifier,
  SerializedPattern,
  SerializedRegex,
} from "@/prisma-package/src/utils/document-type-builtins";
import {
  ClassifiableDefinition,
  scoreDocumentType,
  PatternMatch,
  HIGH_CONFIDENCE_SCORE,
  MEDIUM_CONFIDENCE_SCORE,
} from "./document-type-detection.utils";

// Cap on a STORED classifier's pattern list sizes (enforced by the actions'
// classifierSchema and by pattern merging). Built-ins set the floor — the
// Invoice classifier ships with 47 patterns — so this must comfortably
// exceed them. Distinct from classifier-generation's MAX_PATTERNS (20),
// which bounds what a single LLM generation call may propose. Lives here
// (client-safe) rather than in classifier-generation.utils, which pulls the
// Anthropic SDK into any client bundle importing it.
export const MAX_CLASSIFIER_PATTERNS = 60;

/**
 * Pure helpers for the bulk-corpus document type wizard: scoring a candidate
 * classifier against every document in an uploaded corpus, picking
 * representative samples for LLM pattern generation, and merging suggested
 * patterns into an existing classifier.
 */

// Per-document text kept in browser memory. Long enough that header/body
// patterns still match; bounds memory to ~12MB for a 1000-doc corpus.
export const CORPUS_TEXT_LIMIT = 12_000;

// Max sample texts per LLM pattern-generation call. Lives here (not in the
// actions file, which as "use server" can only export async functions, nor in
// classifier-generation.utils, which pulls the Anthropic SDK client-side).
export const MAX_GENERATION_SAMPLES = 5;

// scoreDocumentType's confidence buckets, re-exported under the corpus
// wizard's vocabulary.
export const QUALIFY_SCORE = HIGH_CONFIDENCE_SCORE;
export const POSSIBLE_SCORE = MEDIUM_CONFIDENCE_SCORE;

// The pattern-generation action Zod-requires each sample text to be at least
// this long (see generateClassifierPatternsAction); shorter texts must be
// filtered out before calling it.
export const MIN_GENERATION_TEXT_LENGTH = 40;

export type CorpusText = {
  fileName: string;
  text: string;
  // Cloudinary public id — the stable per-doc key. fileName is display-only
  // and can collide in a large corpus.
  publicId?: string;
};

export type CorpusDocResult = {
  fileName: string;
  publicId?: string;
  score: number;
  excluded: boolean;
  exclusionReason?: string;
  hasText: boolean;
  // Which patterns matched, for the per-doc "why it scored N" breakdown.
  matches: PatternMatch[];
};

export type CorpusStats = {
  total: number;
  qualifying: number;
  possible: number;
  failing: number;
  skippedNoText: number;
  results: CorpusDocResult[];
};

/**
 * Scores every corpus document against a single candidate classifier and
 * buckets the results. Documents without text are counted separately and
 * excluded from the score buckets.
 */
export const computeCorpusStats = (
  docs: CorpusText[],
  classifier: SerializedClassifier | null
): CorpusStats => {
  const results: CorpusDocResult[] = docs.map((doc) => {
    const base = {
      fileName: doc.fileName,
      publicId: doc.publicId,
      matches: [] as PatternMatch[],
    };
    if (!doc.text) {
      return { ...base, score: 0, excluded: false, hasText: false };
    }
    if (!classifier || classifier.patterns.length === 0) {
      return { ...base, score: 0, excluded: false, hasText: true };
    }
    const detection = scoreDocumentType(
      doc.text,
      [{ key: "CORPUS", classifier }],
      doc.fileName
    );
    const result = detection.results.find((entry) => entry.type === "CORPUS");
    return {
      ...base,
      score: result?.score ?? 0,
      excluded: result?.excluded ?? false,
      exclusionReason: result?.exclusionReason,
      matches: result?.matches ?? [],
      hasText: true,
    };
  });

  const scored = results.filter((result) => result.hasText);
  return {
    total: docs.length,
    qualifying: scored.filter(
      (result) => !result.excluded && result.score >= QUALIFY_SCORE
    ).length,
    possible: scored.filter(
      (result) =>
        !result.excluded &&
        result.score >= POSSIBLE_SCORE &&
        result.score < QUALIFY_SCORE
    ).length,
    failing: scored.filter(
      (result) => result.excluded || result.score < POSSIBLE_SCORE
    ).length,
    skippedNoText: results.filter((result) => !result.hasText).length,
    results,
  };
};

/**
 * Deterministic length-stratified sample selection: sorts docs with usable
 * text by text length and picks n evenly spaced entries, so short and long
 * variants of the type both inform pattern generation. Texts below the
 * generation action's minimum length are excluded — they would fail its
 * input validation.
 */
export const selectRepresentativeSamples = (
  docs: CorpusText[],
  n: number
): CorpusText[] => {
  const withText = docs.filter(
    (doc) => doc.text.length >= MIN_GENERATION_TEXT_LENGTH
  );
  if (withText.length <= n) return withText;

  const sorted = [...withText].sort(
    (a, b) => a.text.length - b.text.length || a.fileName.localeCompare(b.fileName)
  );
  const picked: CorpusText[] = [];
  for (let i = 0; i < n; i++) {
    const index = Math.round((i * (sorted.length - 1)) / (n - 1));
    picked.push(sorted[index]);
  }
  // Evenly spaced indices are distinct because sorted.length > n.
  return picked;
};

/**
 * The k lowest-scoring, non-excluded docs that have text — the ones an
 * improve round should learn from. Excluded docs are a pattern problem, not
 * a coverage problem, so they're left out.
 */
export const selectWorstOffenders = (
  stats: CorpusStats,
  docs: CorpusText[],
  k = 3
): CorpusText[] => {
  const textByKey = new Map(
    docs.map((doc) => [doc.publicId ?? doc.fileName, doc.text])
  );
  return stats.results
    .filter(
      (result) =>
        result.hasText && !result.excluded && result.score < QUALIFY_SCORE
    )
    .sort((a, b) => a.score - b.score)
    .slice(0, k)
    .map((result) => ({
      fileName: result.fileName,
      publicId: result.publicId,
      text: textByKey.get(result.publicId ?? result.fileName) ?? "",
    }))
    .filter((doc) => doc.text.length >= MIN_GENERATION_TEXT_LENGTH);
};

export type MergeResult = {
  classifier: SerializedClassifier;
  added: number;
  dropped: number;
};

const dedupeAppend = <T extends { source: string }>(
  existing: T[],
  suggested: T[],
  cap: number
): { list: T[]; added: number; dropped: number } => {
  const seen = new Set(existing.map((pattern) => pattern.source));
  const fresh = suggested.filter((pattern) => !seen.has(pattern.source));
  const room = Math.max(0, cap - existing.length);
  return {
    list: [...existing, ...fresh.slice(0, room)],
    added: Math.min(fresh.length, room),
    dropped: Math.max(0, fresh.length - room),
  };
};

/**
 * Merges suggested patterns into an existing classifier: dedupes by regex
 * source, appends up to the server-side cap per list, and reports how many
 * were added/dropped. Exclusion patterns are never merged from suggestions
 * (improve mode is prompted not to produce them).
 *
 * Overlay mode passes `capBase`: the pattern count of the global classifier
 * underneath, raising the cap to capBase + MAX so the 60-pattern budget
 * constrains the org's DELTA rather than base + delta — a well-trained
 * global base must not use up an org's training room.
 */
export const mergeClassifierPatterns = (
  existing: SerializedClassifier,
  suggested: {
    patterns?: SerializedPattern[];
    filenamePatterns?: SerializedRegex[];
  },
  options?: { capBase?: number }
): MergeResult => {
  const cap = MAX_CLASSIFIER_PATTERNS + (options?.capBase ?? 0);
  const patterns = dedupeAppend(
    existing.patterns,
    suggested.patterns ?? [],
    cap
  );
  const filenamePatterns = dedupeAppend(
    existing.filenamePatterns ?? [],
    suggested.filenamePatterns ?? [],
    cap
  );
  return {
    classifier: {
      patterns: patterns.list,
      ...(existing.exclusionPatterns &&
        existing.exclusionPatterns.length > 0 && {
          exclusionPatterns: existing.exclusionPatterns,
        }),
      ...(filenamePatterns.list.length > 0 && {
        filenamePatterns: filenamePatterns.list,
      }),
    },
    added: patterns.added + filenamePatterns.added,
    dropped: patterns.dropped + filenamePatterns.dropped,
  };
};

// How many example documents a type keeps after a save/training run — the
// best-scoring docs across the session. Enforced by the actions' Zod
// schemas and surfaced in UI copy ("we save up to 10 documents per type").
export const MAX_SAMPLE_DOCUMENTS = 10;

export type CorpusSampleDoc = {
  fileName: string;
  publicId: string;
  secureUrl: string;
  format?: string;
  text: string;
};

/**
 * The n best sample-document candidates for a saved type: highest-scoring
 * docs with text first, then docs without readable text filling any
 * remaining slots — kept purely for debugging ("why doesn't this scan
 * recognize?"), never displacing a readable doc. Callers must omit the
 * persisted `score` for text-less samples so they don't drag down health.
 * Keys by publicId (falling back to fileName) so duplicate filenames in a
 * large corpus don't cross-assign scores.
 */
export const selectTopScoringSamples = (
  stats: CorpusStats,
  docs: CorpusSampleDoc[],
  n = MAX_SAMPLE_DOCUMENTS
): CorpusSampleDoc[] => {
  const scoreByKey = new Map(
    stats.results.map((result) => [
      result.publicId ?? result.fileName,
      result.excluded ? 0 : result.score,
    ])
  );
  const withText = docs
    .filter((doc) => doc.text.length > 0)
    .map((doc) => ({
      doc,
      score: scoreByKey.get(doc.publicId || doc.fileName) ?? 0,
    }))
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.doc);
  const withoutText = docs.filter((doc) => doc.text.length === 0);
  return [...withText, ...withoutText].slice(0, n);
};

export type BestOtherType = {
  type: string;
  score: number;
};

/**
 * The highest-scoring existing type for a document, used to warn when a doc
 * fits another type better than the one being edited. Returns null when
 * nothing matches. Pure; callers cache per doc since the result does not
 * depend on the draft classifier.
 */
export const computeBestOtherType = (
  text: string,
  fileName: string,
  otherDefinitions: ClassifiableDefinition[]
): BestOtherType | null => {
  if (!text || otherDefinitions.length === 0) return null;
  const detection = scoreDocumentType(text, otherDefinitions, fileName);
  const best = detection.results
    .filter((result) => !result.excluded && result.score > 0)
    .sort((a, b) => b.score - a.score)[0];
  return best ? { type: best.type, score: best.score } : null;
};

// A document known (or confidently recognized) to belong to a type other
// than the one being edited — the negative examples a cross-type check
// scores proposed patterns against.
export type ForeignCorpusDoc = {
  fileName: string;
  publicId?: string;
  text: string;
  typeKey: string;
  typeLabel?: string;
};

export type CrossTypeFlip = {
  fileName: string;
  publicId?: string;
  // The type this doc belongs to.
  typeKey: string;
  typeLabel?: string;
  // What detection said before the proposal (usually typeKey).
  previousType: string;
  // The target type's score under the proposed classifier.
  targetScore: number;
  // Proposed-only pattern sources that matched — the culprits.
  matchedNewPatterns: string[];
};

export type CrossTypeCheckResult = {
  checked: number;
  flips: CrossTypeFlip[];
  // New patterns implicated in at least one flip, most damaging first.
  riskyPatterns: { source: string; flipCount: number }[];
};

/**
 * Scores a proposed classifier against documents belonging to OTHER types
 * and reports the ones whose detected type would flip to the target — the
 * cross-type regressions a single-type improve loop can't see, since it
 * only optimizes against its own corpus. Each flip carries the proposed-only
 * patterns that matched, so a reviewer can drop the over-broad pattern
 * rather than the whole proposal. Pure.
 */
export const checkCrossTypeRegressions = (
  targetKey: string,
  existingClassifier: SerializedClassifier | null,
  proposedClassifier: SerializedClassifier,
  foreignDocs: ForeignCorpusDoc[],
  definitions: ClassifiableDefinition[],
  options?: {
    // Claim threshold the target type will carry in production (e.g. the
    // conservative default for a brand-new type). Definitions' own
    // claimThreshold fields are honored as passed; this covers the target,
    // whose entries this function builds itself.
    targetClaimThreshold?: number | null;
  }
): CrossTypeCheckResult => {
  const others = definitions.filter(
    (definition) => definition.key !== targetKey
  );
  const targetThreshold = options?.targetClaimThreshold ?? null;
  const baselineDefs =
    existingClassifier && existingClassifier.patterns.length > 0
      ? [
          ...others,
          {
            key: targetKey,
            classifier: existingClassifier,
            claimThreshold: targetThreshold,
          },
        ]
      : others;
  const proposedDefs = [
    ...others,
    {
      key: targetKey,
      classifier: proposedClassifier,
      claimThreshold: targetThreshold,
    },
  ];
  const existingSources = new Set([
    ...(existingClassifier?.patterns ?? []).map((pattern) => pattern.source),
    ...(existingClassifier?.filenamePatterns ?? []).map(
      (pattern) => pattern.source
    ),
  ]);

  const flips: CrossTypeFlip[] = [];
  let checked = 0;
  for (const doc of foreignDocs) {
    if (!doc.text) continue;
    checked += 1;
    const proposed = scoreDocumentType(doc.text, proposedDefs, doc.fileName);
    if (proposed.type !== targetKey) continue;
    // Already claimed by the target before the proposal: a pre-existing
    // overlap, not a regression this proposal introduces.
    const baseline = scoreDocumentType(doc.text, baselineDefs, doc.fileName);
    if (baseline.type === targetKey) continue;
    const targetResult = proposed.results.find(
      (result) => result.type === targetKey
    );
    flips.push({
      fileName: doc.fileName,
      publicId: doc.publicId,
      typeKey: doc.typeKey,
      typeLabel: doc.typeLabel,
      previousType: baseline.type,
      targetScore: targetResult?.score ?? 0,
      matchedNewPatterns: (targetResult?.matches ?? [])
        .map((match) => match.source)
        .filter((source) => !existingSources.has(source)),
    });
  }

  const flipCountBySource = new Map<string, number>();
  for (const flip of flips) {
    for (const source of flip.matchedNewPatterns) {
      flipCountBySource.set(source, (flipCountBySource.get(source) ?? 0) + 1);
    }
  }
  return {
    checked,
    flips,
    riskyPatterns: [...flipCountBySource.entries()]
      .map(([source, flipCount]) => ({ source, flipCount }))
      .sort((a, b) => b.flipCount - a.flipCount || a.source.localeCompare(b.source)),
  };
};
